import { useCallback, useEffect, useRef, useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import EmblaCarouselAutoplay from 'embla-carousel-autoplay'

import { RevealItem, ScrollReveal } from '#/components/common/ScrollReveal'
import { TextAnimate } from '#/components/common/TextAnimate'
import { HighlightText } from '#/components/common/HighlightText'
import { ABOUT_TESTIMONIALS_CONTENT } from '#/data/aboutTestimonials'
import { useTestimonials } from '#/features/home/hooks/useTestimonials'
import { Avatar, AvatarImage, AvatarFallback } from '#/components/ui/avatar'
import NoData from '#/components/common/NoData'
import { getImageUrl } from '#/lib/imageUrl'
import { LuChevronLeft, LuChevronRight, LuStar } from 'react-icons/lu'

function getInitials(name: string): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function IslamicGeoBg() {
  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.03]"
      preserveAspectRatio="xMidYMid slice"
      viewBox="0 0 800 500"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {[0, 160, 320, 480, 640, 800].map((x) =>
        [0, 125, 250, 375, 500].map((y) => (
          <g key={`${x}-${y}`} transform={`translate(${x}, ${y})`}>
            <rect
              x="-35"
              y="-35"
              width="70"
              height="70"
              stroke="#FFB606"
              strokeWidth="0.8"
              fill="none"
            />
            <rect
              x="-35"
              y="-35"
              width="70"
              height="70"
              stroke="#FFB606"
              strokeWidth="0.8"
              fill="none"
              transform="rotate(45 0 0)"
            />
            <circle
              cx="0"
              cy="0"
              r="20"
              stroke="#FFB606"
              strokeWidth="0.5"
              fill="none"
            />
          </g>
        )),
      )}
    </svg>
  )
}

function ArabicQuoteMark() {
  return (
    <span
      className="pointer-events-none absolute -top-4 left-4 font-serif text-9xl leading-none text-primary-500/20 select-none"
      aria-hidden="true"
      style={{ fontFamily: 'Philosopher, serif' }}
    >
      ❝
    </span>
  )
}

function StarRating({ count }: { count: number }) {
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: count }).map((_, i) => (
        <LuStar key={i} className="text-primary-400 fill-primary-400 text-sm" />
      ))}
    </div>
  )
}

export default function AboutTestimonialsSection() {
  const { data: testimonials = [], isLoading } = useTestimonials()

  const autoplay = useRef(
    EmblaCarouselAutoplay({
      delay: 5000,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
    }),
  )

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: testimonials.length > 1 },
    [autoplay.current],
  )
  const [selectedIndex, setSelectedIndex] = useState(0)

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    onSelect()
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)
    return () => {
      emblaApi.off('select', onSelect)
      emblaApi.off('reInit', onSelect)
    }
  }, [emblaApi, onSelect])

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev()
    autoplay.current?.reset()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext()
    autoplay.current?.reset()
  }, [emblaApi])

  return (
    <section
      className="relative w-full py-20 sm:py-28 overflow-hidden noir-section scroll-mt-20"
      id="testimonials"
    >
      <IslamicGeoBg />

      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(50% 50% at 50% 100%, rgba(255,182,6,0.07) 0%, transparent 70%)',
        }}
      />

      <div className="container mx-auto flex flex-col gap-14">
        {/* Header */}
        <ScrollReveal className="flex flex-col items-center gap-4 text-center">
          <RevealItem>
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-primary-300">
              {ABOUT_TESTIMONIALS_CONTENT.eyebrow}
            </p>
          </RevealItem>
          <RevealItem className="w-full">
            <h2 className="text-4xl font-bold text-white sm:text-5xl">
              {ABOUT_TESTIMONIALS_CONTENT.title}{' '}
              <HighlightText
                inView
                text={ABOUT_TESTIMONIALS_CONTENT.highlight}
                className="bg-linear-to-r from-primary-600/60 to-primary-800/60 text-white"
              />
            </h2>
          </RevealItem>
          <RevealItem className="max-w-2xl">
            <TextAnimate
              by="word"
              animation="fadeIn"
              duration={1.1}
              className="text-base text-neutral-200 sm:text-lg"
            >
              {ABOUT_TESTIMONIALS_CONTENT.description}
            </TextAnimate>
          </RevealItem>
        </ScrollReveal>

        {/* Carousel / Cards */}
        {isLoading ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-64 w-full animate-pulse rounded-2xl bg-white/5 border border-white/10"
              />
            ))}
          </div>
        ) : testimonials.length === 0 ? (
          <NoData
            title="No Testimonials Yet"
            description="Student reviews and feedback will appear here soon."
          />
        ) : (
          <RevealItem>
            <div className="relative">
              <div ref={emblaRef} className="overflow-hidden">
                <div className="flex ml-[-24px]">
                  {testimonials.map((item, index) => (
                    <div
                      key={item.id || item.name + index}
                      className="relative flex-[0_0_100%] min-w-0 pl-6 md:flex-[0_0_50%] xl:flex-[0_0_33.333%]"
                    >
                      <div className="relative flex h-full flex-col gap-6 rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur-sm">
                        <ArabicQuoteMark />

                        <StarRating count={item.rating || 5} />

                        <blockquote className="flex-1 text-base leading-relaxed text-neutral-200 italic">
                          "{item.quote}"
                        </blockquote>

                        <div className="flex items-center gap-4 border-t border-white/8 pt-5">
                          <Avatar className="size-12 border border-primary-400/40">
                            <AvatarImage
                              src={getImageUrl(item.avatarPath)}
                              alt={item.name}
                            />
                            <AvatarFallback className="bg-primary-900 text-primary-300 font-bold text-base border border-primary-500/30">
                              {getInitials(item.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex flex-col">
                            <p className="text-base font-bold text-white">
                              {item.name}
                            </p>
                            <p className="text-xs font-medium text-primary-400 uppercase tracking-wider mt-0.5">
                              {item.role}{' '}
                              {item.location ? `· ${item.location}` : ''}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Navigation arrows */}
              {testimonials.length > 1 && (
                <div className="mt-8 flex items-center justify-center gap-4">
                  <button
                    type="button"
                    onClick={scrollPrev}
                    aria-label="Previous testimonial"
                    className="flex size-11 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white transition-all hover:border-primary-500/50 hover:bg-primary-500/15 hover:text-primary-400 disabled:opacity-30"
                  >
                    <LuChevronLeft className="text-xl" />
                  </button>

                  <div className="flex items-center gap-2">
                    {testimonials.map((_, index) => (
                      <button
                        key={index}
                        type="button"
                        aria-label={`Go to testimonial ${index + 1}`}
                        onClick={() => {
                          emblaApi?.scrollTo(index)
                          autoplay.current?.reset()
                        }}
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          index === selectedIndex
                            ? 'w-8 bg-primary-500'
                            : 'w-2 bg-white/30 hover:bg-white/50'
                        }`}
                      />
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={scrollNext}
                    aria-label="Next testimonial"
                    className="flex size-11 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white transition-all hover:border-primary-500/50 hover:bg-primary-500/15 hover:text-primary-400 disabled:opacity-30"
                  >
                    <LuChevronRight className="text-xl" />
                  </button>
                </div>
              )}
            </div>
          </RevealItem>
        )}
      </div>
    </section>
  )
}
